<template>
    <div class="app-rich-text-editor" :class="{ 'is-disabled': data.disabled }">
        <editor
            ref="toastUiEditor"
            :initial-value="initialValue"
            :options="editorOptions"
            :height="`${data.height}px`"
            initialEditType="wysiwyg"
            previewStyle="tab"
            @change="onEditorChange"
        />
    </div>
</template>

<script>
import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import { Editor } from '@toast-ui/vue-editor';
import { InputMixin } from './mixin/InputMixin';

export default {
    name: 'RichTextEditor',
    mixins: [InputMixin],
    components: {
        editor: Editor,
    },
    data() {
        return {
            initialValue: this.normalizeValue(this.value),
            syncingExternalValue: false,
            editorOptions: {
                hideModeSwitch: true,
                usageStatistics: false,
                placeholder: this.data.placeholder,
                toolbarItems: this.data.minimal ? [
                    ['heading', 'bold', 'italic'],
                    ['ul', 'ol', 'task'],
                    ['link', 'quote'],
                ] : [
                    ['heading', 'bold', 'italic', 'strike'],
                    ['hr', 'quote'],
                    ['ul', 'ol', 'task', 'indent', 'outdent'],
                    ['table', 'image', 'link'],
                    ['code', 'codeblock'],
                ],
                hooks: {
                    addImageBlobHook: this.handleImageUpload,
                },
            },
        };
    },
    watch: {
        value: {
            handler(newValue) {
                this.syncEditorValue(newValue);
            },
        },
    },
    methods: {
        normalizeValue(value) {
            return typeof value === 'string' ? value : '';
        },
        normalizeEditorOutput(value) {
            const html = this.normalizeValue(value);
            const textContent = html
                .replace(/<p><br><\/p>/gi, '')
                .replace(/&nbsp;/gi, ' ')
                .replace(/<br\s*\/?>/gi, ' ')
                .replace(/<[^>]+>/g, ' ')
                .trim();

            return textContent ? html : '';
        },
        onEditorChange() {
            if (this.syncingExternalValue || !this.$refs.toastUiEditor) {
                return;
            }

            const html = this.$refs.toastUiEditor.invoke('getHTML');

            this.$emit('input', this.normalizeEditorOutput(html));
        },
        syncEditorValue(value) {
            if (!this.$refs.toastUiEditor) {
                this.initialValue = this.normalizeValue(value);
                return;
            }

            const normalizedValue = this.normalizeValue(value);
            const currentValue = this.normalizeEditorOutput(this.$refs.toastUiEditor.invoke('getHTML'));

            if (currentValue === this.normalizeEditorOutput(normalizedValue)) {
                return;
            }

            this.syncingExternalValue = true;
            this.$refs.toastUiEditor.invoke('setHTML', normalizedValue);
            this.$nextTick(() => {
                this.syncingExternalValue = false;
            });
        },
        handleImageUpload(blob, callback) {
            if (!(blob instanceof Blob)) {
                return false;
            }

            const reader = new FileReader();

            reader.onload = () => {
                callback(reader.result, blob.name || 'image');
            };

            reader.readAsDataURL(blob);

            return false;
        },
    },
};
</script>

<style lang="scss">
.app-rich-text-editor {
    .toastui-editor-defaultUI {
        border-color: var(--default-border-color);
        border-radius: 0.85rem;
        overflow: hidden;
        background: var(--form-control-bg);
        transition: border-color 0.2s ease, background-color 0.2s ease;
    }

    .toastui-editor-toolbar {
        background: linear-gradient(180deg, rgba(var(--brand-primary-rgb), 0.05), rgba(var(--brand-primary-rgb), 0.02));
        border-bottom-color: var(--default-border-color);
        padding: 0.35rem 0.45rem;
    }

    .toastui-editor-toolbar-icons {
        border-radius: 0.55rem;
    }

    .toastui-editor-toolbar-group {
        margin: 0.15rem 0.2rem;
        border-right-color: rgba(var(--brand-primary-rgb), 0.12);
    }

    .toastui-editor-md-tab-container,
    .toastui-editor-defaultUI-toolbar {
        background: var(--base-color);
    }

    .toastui-editor-ww-container,
    .toastui-editor-md-container,
    .toastui-editor-main-container,
    .toastui-editor-main {
        background: var(--form-control-bg);
    }

    .toastui-editor-contents,
    .toastui-editor-contents p,
    .toastui-editor-contents li,
    .toastui-editor-contents h1,
    .toastui-editor-contents h2,
    .toastui-editor-contents h3,
    .toastui-editor-contents h4,
    .toastui-editor-contents h5,
    .toastui-editor-contents h6,
    .ProseMirror {
        color: var(--default-font-color);
    }

    .toastui-editor-contents {
        font-size: 0.97rem;
        line-height: 1.72;
    }

    .toastui-editor-defaultUI.focused {
        border-color: var(--brand-primary);
        box-shadow: none;
    }

    .toastui-editor-toolbar-group button:hover,
    .toastui-editor-toolbar-group button:focus {
        background-color: rgba(var(--brand-primary-rgb), 0.08);
    }

    .toastui-editor-toolbar-group button.active,
    .toastui-editor-toolbar-group button:active {
        background-color: rgba(var(--brand-primary-rgb), 0.14);
    }

    .toastui-editor-popup,
    .toastui-editor-context-menu,
    .toastui-editor-dropdown-toolbar {
        background: var(--base-color);
        border-color: var(--default-border-color);
        color: var(--default-font-color);
    }

    .toastui-editor-popup-body input {
        color: var(--default-font-color);
        background: var(--form-control-bg);
        border-color: var(--default-border-color);
    }

    .toastui-editor-popup-body label {
        color: var(--default-font-color);
    }

    &.is-disabled {
        opacity: 0.75;
        pointer-events: none;
    }
}
</style>
